import React from 'react';

import styled, { createGlobalStyle } from 'styled-components';

import LogoBsa from '../../assets/logo200.png';
import moment from 'moment';
import { getProductNameByInternalCode } from '../../utils/getProductConfig';
import { formatDateBrWithTime } from '../../utils/formatDate';
import { createPortal } from 'react-dom';

type CompartmentReport = {
  compartimentoId: number;
  inicio: string | null;
  fim: string | null;
  duracaoMinutos: number | null;
  baia: number | null;
  senha: number | null;
  produto: string;
  volumeTemperaturaAmbiente: number | null;
  volumeCorrigido20: number | null;
  temperaturaCarregamento: number | null;
  massaEspecifica: number | null;
  grauInpm: number | null;
  peso: number | null;
};

type PlacaReport = {
  ordem: number;
  cliente: string;
  motorista: string;
  placa: string;
  produto: string;
  inicio: string | null;
  fim: string | null;
  duracaoMinutos: number | null;
  volumeTotalTemperaturaAmbiente: number;
  volumeTotalCorrigido20: number;
  compartimentos: CompartmentReport[];
};

type OperationsReportData = {
  placas: PlacaReport[];
};

type OperationsReportProps = {
  date: string;
  reportData: OperationsReportData;
};

type ReportField = {
  label: string;
  name: keyof CompartmentReport;
  format?: (
    value: CompartmentReport[keyof CompartmentReport],
    row: CompartmentReport,
  ) => React.ReactNode;
};

const OperationsReportPrintStyles = createGlobalStyle`
  @media print {
    body > *:not(#report-content) {
      display: none !important;
    }

    #report-content,
    #report-content * {
      visibility: visible !important;
    }

    #report-content {
      display: block !important;
      position: static !important;

      width: 100% !important;
      height: auto !important;
      min-height: 0 !important;

      margin: 0 !important;
      padding: 0 !important;

      overflow: visible !important;
    }

    @page {
      size: A4 landscape;
      margin: 10mm;
    }
  }
`;

export const OperationsReport = ({
  date,
  reportData,
}: OperationsReportProps) => {
  const formatNumber = (value: number | null, decimalPlaces = 0): string => {
    if (value === null || value === undefined) {
      return '-';
    }

    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  };

  const formatValue = (
    value: CompartmentReport[keyof CompartmentReport],
  ): React.ReactNode => {
    return value ?? '-';
  };

  const fields: ReportField[] = [
    {
      label: 'Baia',
      name: 'baia',
    },
    {
      label: 'Senha',
      name: 'senha',
    },
    {
      label: 'Produto',
      name: 'produto',
      format: value => getProductNameByInternalCode(Number(value)),
    },
    {
      label: 'Início da operação',
      name: 'inicio',
      format: value => formatDateBrWithTime(value as string, true) ?? null,
    },
    {
      label: 'Fim da operação',
      name: 'fim',
      format: value => formatDateBrWithTime(value as string, true) ?? null,
    },
    {
      label: 'Vol. temp. ambiente (L)',
      name: 'volumeTemperaturaAmbiente',
      format: value => formatNumber(value as number | null),
    },
    {
      label: 'Vol. corrigido a 20°C (L)',
      name: 'volumeCorrigido20',
      format: value => formatNumber(value as number | null),
    },
    {
      label: 'Temp. carregamento (°C)',
      name: 'temperaturaCarregamento',
      format: value => formatNumber(value as number | null, 2),
    },
    {
      label: 'Massa específica',
      name: 'massaEspecifica',
      format: value => formatNumber(value as number | null, 2),
    },
    {
      label: 'Grau INPM',
      name: 'grauInpm',
      format: value => formatNumber(value as number | null, 2),
    },
    {
      label: 'Peso (kg)',
      name: 'peso',
      format: value => formatNumber(value as number | null),
    },
  ];

  const report = (
    <>
      <OperationsReportPrintStyles />

      <Container id="report-content">
        <ReportPage>
          <ReportHeader>
            <LogoContainer>
              <img
                src={LogoBsa}
                style={{
                  display: 'flex',
                  objectFit: 'contain',
                  width: '100%',
                  height: '100%',
                }}
              />
            </LogoContainer>

            <HeaderContainer>
              <Text>BOM SUCESSO AGROINDUSTRIA SA</Text>
              <Text>(64) 3051-5090</Text>
              <Text>RELATÓRIO DE OPERAÇÃO</Text>
            </HeaderContainer>

            <DateContainer>{moment(date).format('DD/MM/YYYY')}</DateContainer>
          </ReportHeader>

          <ReportContent>
            {reportData.placas.map((carregamento, index) => (
              <PlateSection key={`${carregamento.ordem}-${carregamento.placa}`}>
                {index === 0 && (
                  <ContentHeader>
                    <div>
                      <HeaderLabel>Cliente</HeaderLabel>
                      <HeaderValue>{carregamento.cliente}</HeaderValue>
                    </div>

                    <div>
                      <HeaderLabel>Motorista</HeaderLabel>
                      <HeaderValue>{carregamento.motorista}</HeaderValue>
                    </div>
                  </ContentHeader>
                )}

                <SubHeader>
                  <SubHeaderCol>
                    <SubHeaderLabel>Placa</SubHeaderLabel>
                    <SubHeaderValue>{carregamento.placa}</SubHeaderValue>
                  </SubHeaderCol>

                  <SubHeaderCol>
                    <SubHeaderLabel>Inicio</SubHeaderLabel>
                    <SubHeaderValue>
                      {formatDateBrWithTime(
                        carregamento.inicio as string,
                        true,
                      )}
                    </SubHeaderValue>
                  </SubHeaderCol>

                  <SubHeaderCol>
                    <SubHeaderLabel>Fim</SubHeaderLabel>
                    <SubHeaderValue>
                      {formatDateBrWithTime(carregamento.fim as string, true)}
                    </SubHeaderValue>
                  </SubHeaderCol>

                  <SubHeaderCol>
                    <SubHeaderLabel>Duração Minutos</SubHeaderLabel>
                    <SubHeaderValue>
                      {carregamento.duracaoMinutos}
                    </SubHeaderValue>
                  </SubHeaderCol>

                  <SubHeaderCol>
                    <SubHeaderLabel>Vol. Total Temp. Ambiente</SubHeaderLabel>
                    <SubHeaderValue>
                      {formatNumber(
                        carregamento.volumeTotalTemperaturaAmbiente,
                      )}
                    </SubHeaderValue>
                  </SubHeaderCol>

                  <SubHeaderCol>
                    <SubHeaderLabel>Vol. Total a 20°C</SubHeaderLabel>
                    <SubHeaderValue>
                      {formatNumber(carregamento.volumeTotalCorrigido20)}
                    </SubHeaderValue>
                  </SubHeaderCol>
                </SubHeader>

                <DetailsContainer>
                  <HeaderRow>
                    {fields.map(header => (
                      <HeaderField key={header.label}>
                        {header.label}
                      </HeaderField>
                    ))}
                  </HeaderRow>
                  {carregamento.compartimentos.map(compartimento => (
                    <CompartimentRow key={compartimento.compartimentoId}>
                      {fields.map(field => {
                        const value = compartimento[field.name];

                        return (
                          <CompartimentField key={field.name}>
                            {field.format
                              ? field.format(value, compartimento)
                              : formatValue(value)}
                          </CompartimentField>
                        );
                      })}
                    </CompartimentRow>
                  ))}
                </DetailsContainer>
              </PlateSection>
            ))}
          </ReportContent>
        </ReportPage>
      </Container>
    </>
  );

  return createPortal(report, document.body);
};

const Container = styled.div`
  display: none;

  @media print {
    display: block !important;
    visibility: visible !important;

    position: static !important;

    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;

    margin: 0 !important;
    padding: 0 !important;

    overflow: visible !important;
    background: white;
  }
`;

const ReportPage = styled.div`
  @media print {
    width: 100%;
    height: auto;
    min-height: 0;

    break-after: auto;
    page-break-after: auto;
  }
`;

const PlateSection = styled.div`
  @media print {
    break-inside: avoid;
    page-break-inside: avoid;
  }
`;

const ReportHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  width: 100%;

  padding: 0 0.5rem;
`;

const LogoContainer = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 126px;
  height: 100%;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;

  gap: 0.5rem;
`;

const Text = styled.p`
  padding: 0;
  margin: 0;

  font-size: 1.5rem;
  font-weight: 600;

  text-align: center;
`;

const DateContainer = styled.div`
  display: flex;

  align-items: flex-end;

  font-size: 1.5rem;
  font-weight: 600;
`;

const ReportContent = styled.div`
  display: flex;
  flex-direction: column;

  padding: 1rem 0 0 0;
  margin-top: 1rem;

  border: 1px solid grey;
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: center;

  gap: 3rem;
`;

const HeaderLabel = styled.span`
  display: flex;
  padding: 0 0 0.5rem 0;
`;

const HeaderValue = styled.p`
  margin: 0;
  padding: 0 0 0.5rem 0;
  font-weight: 600;
`;

const SubHeader = styled.div`
  display: flex;
  justify-content: center;

  gap: 1.5rem;

  background: lightgray;

  padding: 1rem;

  border-top: 1px solid gray;
  border-bottom: 1px solid gray;
`;

const SubHeaderCol = styled.div`
  display: flex;

  gap: 0.5rem;
`;

const SubHeaderLabel = styled.span`
  display: flex;

  justify-content: center;

  font-size: 0.75rem;
`;

const SubHeaderValue = styled.p`
  display: flex;

  font-weight: 600;
  font-size: 0.75rem;

  justify-content: center;

  margin: 0;
  padding: 0;
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  width: 100%;

  justify-content: space-between;
`;

const HeaderField = styled.p`
  display: flex;

  margin: 0;
  padding: 0;

  justify-content: center;
  align-items: center;

  text-align: center;
  font-weight: 600;
  font-size: 0.75rem;

  flex: 1 0 0;

  border-bottom: 1px solid grey;

  &:not(:last-child) {
    border-right: 1px solid grey;
  }
`;

const CompartimentRow = styled.div`
  display: flex;

  width: 100%;

  justify-content: space-between;

  &:not(:last-child) {
    border-bottom: 1px solid grey;
  }
`;

const CompartimentField = styled.p`
  display: flex;

  margin: 0;
  padding: 0.5rem 0;

  text-align: center;
  justify-content: center;
  align-items: center;

  font-size: 0.75rem;

  flex: 1 0 0;

  &:not(:last-child) {
    border-right: 1px solid grey;
  }
`;
